import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { initialCartItems, CartItem } from "@/mocks/cart";

const CartPage = () => {
  const [cartItems, setCartItems] = useState<CartItem[]>(initialCartItems);
  const [ordered, setOrdered] = useState(false);

  const updateQty = (id: string, delta: number) => {
    setCartItems((prev) =>
      prev.map((i) => i.id === id ? { ...i, quantity: Math.max(1, i.quantity + delta) } : i)
    );
  };
  const remove = (id: string) => setCartItems((prev) => prev.filter((i) => i.id !== id));
  const subtotal = cartItems.reduce((s, i) => s + i.price * i.quantity, 0);
  const shipping = subtotal >= 50000 ? 0 : 3000;
  const total = subtotal + shipping;

  if (ordered) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar cartCount={0} />
        <main className="pt-32 pb-20 flex flex-col items-center justify-center min-h-[60vh]">
          <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mb-6">
            <i className="ri-check-line text-3xl text-emerald-600" />
          </div>
          <h2 className="text-2xl font-black text-stone-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>주문 완료!</h2>
          <p className="text-stone-500 text-sm mb-8">주문이 성공적으로 접수되었습니다.</p>
          <Link to="/products" className="bg-stone-900 text-white text-sm font-semibold px-8 py-3 rounded-full hover:bg-stone-700 transition-colors whitespace-nowrap cursor-pointer">
            쇼핑 계속하기
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar cartCount={cartItems.reduce((s, i) => s + i.quantity, 0)} />
      <main className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-10">
            <p className="text-xs font-bold tracking-widest uppercase text-stone-400 mb-2">My Cart</p>
            <h1 className="text-3xl md:text-4xl font-black text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>장바구니</h1>
          </div>

          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24">
              <div className="w-16 h-16 flex items-center justify-center bg-stone-100 rounded-full mb-4">
                <i className="ri-shopping-bag-line text-2xl text-stone-400" />
              </div>
              <p className="text-stone-500 text-sm mb-6">장바구니가 비어있어요</p>
              <Link to="/products" className="bg-stone-900 text-white text-sm font-semibold px-8 py-3 rounded-full hover:bg-stone-700 transition-colors whitespace-nowrap cursor-pointer">
                쇼핑하러 가기
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              {/* Items */}
              <div className="lg:col-span-2 space-y-4">
                {cartItems.map((item) => (
                  <div key={item.id} className="flex gap-4 p-4 bg-stone-50 rounded-xl">
                    <div className="w-20 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-white">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-medium text-stone-400 tracking-widest uppercase">{item.category}</p>
                      <p className="text-sm font-semibold text-stone-800 mt-0.5 mb-3">{item.name}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <button onClick={() => updateQty(item.id, -1)} className="w-7 h-7 flex items-center justify-center border border-stone-200 rounded-lg hover:bg-stone-100 cursor-pointer text-stone-600">
                            <i className="ri-subtract-line text-xs" />
                          </button>
                          <span className="text-sm font-semibold w-6 text-center">{item.quantity}</span>
                          <button onClick={() => updateQty(item.id, 1)} className="w-7 h-7 flex items-center justify-center border border-stone-200 rounded-lg hover:bg-stone-100 cursor-pointer text-stone-600">
                            <i className="ri-add-line text-xs" />
                          </button>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-bold text-stone-900">{(item.price * item.quantity).toLocaleString()}원</span>
                          <button onClick={() => remove(item.id)} className="w-7 h-7 flex items-center justify-center text-stone-400 hover:text-rose-500 transition-colors cursor-pointer">
                            <i className="ri-delete-bin-line text-sm" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary */}
              <div className="lg:col-span-1">
                <div className="bg-stone-50 rounded-xl p-6 sticky top-24">
                  <h3 className="text-base font-bold text-stone-900 mb-6">주문 요약</h3>
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-500">상품 금액</span>
                      <span className="text-stone-800">{subtotal.toLocaleString()}원</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-500">배송비</span>
                      <span className={shipping === 0 ? "text-emerald-600 font-medium" : "text-stone-800"}>
                        {shipping === 0 ? "무료" : `${shipping.toLocaleString()}원`}
                      </span>
                    </div>
                    {shipping > 0 && (
                      <p className="text-[11px] text-stone-400">{(50000 - subtotal).toLocaleString()}원 더 담으면 무료배송!</p>
                    )}
                  </div>
                  <div className="flex justify-between font-bold text-base border-t border-stone-200 pt-4 mb-6">
                    <span>총 결제금액</span>
                    <span>{total.toLocaleString()}원</span>
                  </div>
                  <button
                    onClick={() => setOrdered(true)}
                    className="w-full bg-stone-900 text-white text-sm font-bold py-3.5 rounded-xl hover:bg-stone-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    결제하기
                  </button>
                  <Link to="/products" className="block text-center text-xs text-stone-400 mt-3 hover:text-stone-600 transition-colors cursor-pointer">
                    쇼핑 계속하기
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CartPage;
